import React, { useState } from "react"
import { makeStyles } from "@material-ui/core/styles"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import Paper from "@material-ui/core/Paper"
import Tooltip from "@material-ui/core/Tooltip"
import Pagination from "@material-ui/lab/Pagination"
import IconButton from "@material-ui/core/IconButton"
import Button from "@material-ui/core/Button"
import TextField from "@material-ui/core/TextField"
import Checkbox from "@material-ui/core/Checkbox"
import MenuItem from "@material-ui/core/MenuItem"
import DeleteIcon from "@material-ui/icons/DeleteOutline"
import EditIcon from "@material-ui/icons/Edit"
import AddIcon from "@material-ui/icons/Add"
import CheckIcon from "@material-ui/icons/Check"
import CloseIcon from "@material-ui/icons/Close"
import moment from "moment"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"
import { validateCNPJ, validateCPF } from "../utils/utils"

const useStyles = makeStyles((theme) => ({
  tableStyle: {
    border: "none",
    boxShadow: "none",
  },
  tableText: {
    fontFamily: "Open Sans",
    fontWeight: "normal",
    fontSize: "12px",
    lineHeight: "16px",
    color: "#141414",
    textAlign: "center",
    margin: "10px",
    padding: "6px",
    minWidth: "100px",
  },
  tableHeader: {
    textAlign: "center",
    fontFamily: "Open Sans",
    fontStyle: "normal",
    fontWeight: "normal",
    color: "#6F6F6F",
    fontSize: "14px",
    lineHeight: "19px",
    padding: "6px",
  },
  even: {
    background: "#F3F3F3",
  },
  odd: {
    background: "#FFFFFF",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
  },
  badge: {
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "11px",
    margin: "0 4px",
    display: "inline-block",
    cursor: "help",
    textAlign: "center",
    minWidth: "30px",
  },
  badgeGreen: {
    backgroundColor: "#6dd67d55",
    color: "#00C41D",
  },
  badgeRed: {
    backgroundColor: "#ff828255",
    color: "#A60505",
    fontWeight: "bold",
  },
  smallInput: {
    fontSize: "12px",
    "& .MuiInputBase-input": {
        fontSize: "12px",
        padding: "4px 0 5px",
        textAlign: "center"
    }
  },
  actionCell: {
      width: "90px",
      padding: "0px 8px",
      whiteSpace: "nowrap"
  }
}))

function OccurrenceInvolvedPartiesTable(props) {
  const { t } = useTranslation()
  const classes = useStyles()

  const { involvedParties = [], onChange, readOnly } = props

  const pageSize = 10
  const [page, setPage] = useState(1)

  const [isAdding, setIsAdding] = useState(false)
  const [newRowData, setNewRowData] = useState({
      document_number: "",
      name: "",
      type: "",
      branch_number: "",
      branch_name: "",
      account_open_date: null,
      register_update_date: null,
      is_obligated_person: false,
      is_pep: false,
      public_employee_status: "not_public_employee"
  })

  const [editIndex, setEditIndex] = useState(-1)
  const [editRowData, setEditRowData] = useState({})

  const [errorState, setErrorState] = useState({})

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    setEditIndex(-1)
    setErrorState({})
    setIsAdding(false)
  }

  const handleDelete = (indexToDelete) => {
      const actualIndex = (page - 1) * pageSize + indexToDelete
      const newList = [...involvedParties]
      newList.splice(actualIndex, 1)
      if (onChange) onChange(newList)
  }

  const handleStartAdd = () => {
      setPage(1)
      setIsAdding(true)
      setEditIndex(-1)
      setErrorState({})
      setNewRowData({
        document_number: "",
        name: "",
        type: "",
        branch_number: "",
        branch_name: "",
        account_number: "",
        account_open_date: "",
        register_update_date: "",
        is_obligated_person: false,
        is_pep: false,
        public_employee_status: "not_public_employee"
      })
  }

  const handleCancelAdd = () => {
      setIsAdding(false)
      setErrorState({})
  }

  const validateRow = (data) => {
      const errors = {}
      const cleanDoc = (data.document_number || "").replace(/\D/g, "")

      if (!cleanDoc) {
          errors.document_number = t("Obrigatório")
      } else if (cleanDoc.length <= 11) {
          if (!validateCPF(cleanDoc)) errors.document_number = t("CPF Inválido")
      } else {
          if (!validateCNPJ(cleanDoc)) errors.document_number = t("CNPJ Inválido")
      }

      if (!data.name || data.name.trim() === "") errors.name = true
      if (!data.type) errors.type = true

      if (data.type === 'holder') {
          if (!data.branch_number) errors.branch_number = true
          if (!data.branch_name) errors.branch_name = true
          if (!data.account_number) errors.account_number = true
          if (!data.account_open_date) errors.account_open_date = true
          if (!data.register_update_date) errors.register_update_date = true
      }

      return errors
  }

  const cleanRowData = (data) => {
    if (data.branch_number === "") delete data.branch_number
    if (data.branch_name === "") delete data.branch_name
    if (data.account_number === "") delete data.account_number
    if (data.account_open_date === "") delete data.account_open_date
    if (data.register_update_date === "") delete data.register_update_date
    return data
  }

  const handleConfirmAdd = () => {
      const errors = validateRow(newRowData)
      if (Object.keys(errors).length > 0) {
          setErrorState(errors)
          return
      }

      const cleanedData = cleanRowData(newRowData)
      const newList = [cleanedData, ...involvedParties]
      if (onChange) onChange(newList)
      setIsAdding(false)
      setErrorState({})
  }

  const handleNewRowChange = (field, value) => {
      setNewRowData(prev => ({ ...prev, [field]: value }))
      if (errorState[field]) {
          setErrorState(prev => ({ ...prev, [field]: null }))
      }
  }

  const handleStartEdit = (index, party) => {
      setEditIndex(index)
      setIsAdding(false)
      setErrorState({})
      setEditRowData({ ...party })
  }

  const handleCancelEdit = () => {
      setEditIndex(-1)
      setEditRowData({})
      setErrorState({})
  }

  const handleConfirmEdit = () => {
      const errors = validateRow(editRowData)
      if (Object.keys(errors).length > 0) {
          setErrorState(errors)
          return
      }

      const actualIndex = (page - 1) * pageSize + editIndex
      const newList = [...involvedParties]
      const cleanedData = cleanRowData(editRowData)
      newList[actualIndex] = cleanedData

      if (onChange) onChange(newList)
      setEditIndex(-1)
      setErrorState({})
  }

  const handleEditRowChange = (field, value) => {
      setEditRowData(prev => ({ ...prev, [field]: value }))
      if (errorState[field]) {
          setErrorState(prev => ({ ...prev, [field]: null }))
      }
  }

  const startIndex = (page - 1) * pageSize
  const endIndex = page * pageSize
  const currentData = involvedParties.slice(startIndex, endIndex)
  const pageCount = Math.ceil(involvedParties.length / pageSize)

  const getRowClass = (index) => {
    return index % 2 === 0 ? classes.even : classes.odd
  }

  const renderStatusBadge = (label, isActive, activeText, inactiveText) => {
    const badgeClass = `${classes.badge} ${
      isActive ? classes.badgeGreen : classes.badgeRed
    }`
    const tooltipText = isActive ? activeText : inactiveText

    return (
      <Tooltip title={tooltipText} placement="top">
        <span className={badgeClass}>{label}</span>
      </Tooltip>
    )
  }

  const involvedPartyTypes = [
    "beneficiary",
    "depositor",
    "manager",
    "depository_institution",
    "paying_institution",
    "receiving_institution",
    "others",
    "legal_representative",
    "sender",
    "responsible_party",
    "drawer",
    "partner",
    "holder"
  ]

  const renderInputCells = (data, handleChangeHandler, onConfirm, onCancel, rowClass) => {
      return (
          <TableRow className={rowClass} style={{ border: "2px solid #2196f3" }}>
              <TableCell className={classes.tableText}>
                  <Tooltip title={errorState.document_number || ""} open={!!errorState.document_number} placement="top" arrow>
                    <div>
                        <NumberFormat
                            customInput={TextField}
                            placeholder="Doc"
                            value={data.document_number}
                            format={
                              data.document_number && data.document_number.replace(/\D/g, "").length > 11
                              ? "##.###.###/####-##"
                              : "###.###.###-#####"
                            }
                            onValueChange={(values) => {
                                handleChangeHandler("document_number", values.formattedValue)
                            }}
                            error={!!errorState.document_number}
                            className={classes.smallInput}
                        />
                    </div>
                  </Tooltip>
              </TableCell>

              <TableCell className={classes.tableText}>
                  <TextField
                    placeholder="Nome"
                    value={data.name}
                    onChange={(e) => handleChangeHandler("name", e.target.value)}
                    error={!!errorState.name}
                    className={classes.smallInput}
                  />
              </TableCell>

              <TableCell className={classes.tableText}>
                  <TextField
                    select
                    value={data.type}
                    onChange={(e) => handleChangeHandler("type", e.target.value)}
                    error={!!errorState.type}
                    className={classes.smallInput}
                    fullWidth
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) => value ? t(value) : <span style={{ color: "#aaa" }}>Tipo</span>
                    }}
                  >
                    {involvedPartyTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                            {t(option)}
                        </MenuItem>
                    ))}
                  </TextField>
              </TableCell>

              <TableCell className={classes.tableText}>
                  <div style={{display:'flex', gap: '4px'}}>
                    <NumberFormat
                        customInput={TextField}
                        placeholder="Nº Ag"
                        value={data.branch_number}
                        format="####"
                        onValueChange={(values) => {
                            handleChangeHandler("branch_number", values.value)
                        }}
                        onBlur={() => {
                            if (data.branch_number) {
                                handleChangeHandler("branch_number", data.branch_number.padStart(4, "0"))
                            }
                        }}
                        error={!!errorState.branch_number}
                        className={classes.smallInput}
                        style={{width: '40px'}}
                    />
                    <TextField
                        placeholder="Nome Ag"
                        value={data.branch_name}
                        onChange={(e) => handleChangeHandler("branch_name", e.target.value)}
                        error={!!errorState.branch_name}
                        className={classes.smallInput}
                    />
                  </div>
              </TableCell>
              <TableCell className={classes.tableText}>
                  <TextField
                    placeholder={t("Nº Conta")}
                    value={data.account_number}
                    onChange={(e) => handleChangeHandler("account_number", e.target.value)}
                    error={!!errorState.account_number}
                    className={classes.smallInput}
                  />
              </TableCell>

              <TableCell className={classes.tableText}>
                 <TextField
                    type="date"
                    value={data.account_open_date ? moment(data.account_open_date).format("YYYY-MM-DD") : ""}
                    onChange={(e) => handleChangeHandler("account_open_date", e.target.value ? moment(e.target.value).toISOString() : null)}
                    error={!!errorState.account_open_date}
                    className={classes.smallInput}
                 />
              </TableCell>
              <TableCell className={classes.tableText}>
                 <TextField
                    type="date"
                    value={data.register_update_date ? moment(data.register_update_date).format("YYYY-MM-DD") : ""}
                    onChange={(e) => handleChangeHandler("register_update_date", e.target.value ? moment(e.target.value).toISOString() : null)}
                    error={!!errorState.register_update_date}
                    className={classes.smallInput}
                 />
              </TableCell>
              <TableCell className={classes.tableText}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                    <Tooltip title="PO">
                        <Checkbox
                            checked={data.is_obligated_person}
                            onChange={(e) => handleChangeHandler("is_obligated_person", e.target.checked)}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                    <Tooltip title="PEP">
                        <Checkbox
                            checked={data.is_pep}
                            onChange={(e) => handleChangeHandler("is_pep", e.target.checked)}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                    <Tooltip title="SP">
                        <Checkbox
                            checked={data.public_employee_status !== "not_public_employee"}
                            onChange={(e) => handleChangeHandler("public_employee_status", e.target.checked ? "public_employee" : "not_public_employee")}
                            color="primary"
                            size="small"
                            style={{padding: 0}}
                        />
                    </Tooltip>
                  </div>
              </TableCell>
              <TableCell className={classes.actionCell}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={onConfirm} style={{ color: "green" }}>
                        <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={onCancel} style={{ color: "red" }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
              </TableCell>
          </TableRow>
      )
  }

  const isAddButtonEnabled = (!readOnly && !isAdding && editIndex === -1)

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px', paddingLeft: '8px' }}>
            <Button
            startIcon={<AddIcon />}
            onClick={handleStartAdd}
            size="small"
            color="primary"
            style={{ textTransform: 'none', fontWeight: 'bold' }}
            disabled={!isAddButtonEnabled}
            >
                {t("Adicionar parte envolvida")}
            </Button>
        </div>

      <Paper className={classes.tableStyle}>
        <Table style={{ padding: "8px" }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>{t("Nº Documento")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Nome")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Tipo de Envolvido")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Agência")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Nº Conta")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Abertura da Conta")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Última Atualização")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Status")}</TableCell>
              {!readOnly && <TableCell className={classes.tableHeader}></TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {isAdding && renderInputCells(newRowData, handleNewRowChange, handleConfirmAdd, handleCancelAdd, classes.odd)}

            {(!involvedParties || involvedParties.length === 0) && !isAdding ? (
               <TableRow>
                   <TableCell colSpan={8}>
                        <div className="emptyContainer" style={{ padding: "10px", textAlign: "center" }}>
                            <p className={["blueText", "subtitleSize"].join(" ")}>
                            {t("Nenhuma parte envolvida")}
                            </p>
                        </div>
                   </TableCell>
               </TableRow>
            ) : (
                currentData.map((party, index) => {
                    if (index === editIndex) {
                        return renderInputCells(editRowData, handleEditRowChange, handleConfirmEdit, handleCancelEdit, getRowClass(index))
                    }

                    const isPO = party.is_obligated_person
                    const isPEP = party.is_pep
                    const isSP = party.public_employee_status !== "not_public_employee"

                    return (
                        <TableRow
                        key={party.document_number || index}
                        className={getRowClass(index)}
                        >
                        <TableCell className={classes.tableText}>
                            {party.document_number}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.name}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {t(party.type)}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {`${party.branch_number || ""} - ${party.branch_name || ""}`}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.account_number || ""}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.account_open_date ? moment(party.account_open_date).format("DD/MM/YYYY") : "-"}
                        </TableCell>
                        <TableCell className={classes.tableText}>
                            {party.register_update_date ? moment(party.register_update_date).format("DD/MM/YYYY") : "-"}
                        </TableCell>
                        <TableCell className={classes.tableText} style={{ minWidth: "160px" }}>
                            {renderStatusBadge("PO", isPO, `${t("É")} ${t("pessoa obrigada")}`, `${t("Não é")} ${t("pessoa obrigada")}`)}
                            {renderStatusBadge("PEP", isPEP, `${t("É")} ${t("pessoa exposta politicamente")}`, `${t("Não é")} ${t("pessoa exposta politicamente")}`)}
                            {renderStatusBadge("SP", isSP, `${t("É")} ${t("servidor público")}`, `${t("Não é")} ${t("servidor público")}`)}
                        </TableCell>

                        {!readOnly && (
                            <TableCell className={classes.actionCell}>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title={t("Editar")} placement="top">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleStartEdit(index, party)}
                                                disabled={isAdding || editIndex !== -1}
                                                style={{ opacity: (isAdding || editIndex !== -1) ? 0.3 : 1 }}
                                            >
                                                <EditIcon fontSize="small" style={{ color: "#0b1f82" }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title={t("Remover")} placement="top">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(index)}
                                                disabled={isAdding || editIndex !== -1}
                                                style={{ opacity: (isAdding || editIndex !== -1) ? 0.3 : 1 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </div>
                            </TableCell>
                        )}
                        </TableRow>
                    )
                })
            )}
          </TableBody>
        </Table>
      </Paper>

      {involvedParties && involvedParties.length > 0 && (
        <div className={classes.paginationContainer}>
            <Pagination
            className="pagination"
            page={page}
            count={pageCount}
            onChange={handleChangePage}
            />
        </div>
      )}
    </div>
  )
}

export default OccurrenceInvolvedPartiesTable